import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { Variables } from "@/variables";

// Authority is the IAM service root — OIDC discovery at /.well-known/openid-configuration
const OIDC_AUTHORITY = Variables.IAM_SERVICE.replace(/\/+$/, "");

// Lazy-initialized to avoid `window is not defined` during React Router SPA build
let _userManager: UserManager | null = null;

export function getUserManager(): UserManager {
  if (!_userManager) {
    _userManager = new UserManager({
      authority: OIDC_AUTHORITY,
      client_id: Variables.CLIENT,
      redirect_uri: `${window.location.origin}/callback`,
      // Redirect to IAM's branded logout page which force-clears all
      // OIDC session cookies — without this, stale cookies cause IAM
      // to silently re-authenticate on next visit (CEN-854)
      post_logout_redirect_uri: `${OIDC_AUTHORITY}/logout-success`,
      silent_redirect_uri: `${window.location.origin}/silent-renew.html`,
      scope: "openid profile email",
      response_type: "code",
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
  }
  return _userManager;
}

export async function getAuthenticatedUser(): Promise<User | null> {
  const um = getUserManager();
  let user = await um.getUser();
  if (user && !user.expired) return user;
  try {
    user = await um.signinSilent();
    return user ?? null;
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  const storageKey = `oidc.user:${OIDC_AUTHORITY}:${Variables.CLIENT}`;
  const raw = localStorage.getItem(storageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed.expires_at && parsed.expires_at < Date.now() / 1000) return null;
    // IAM's oidc-provider issues opaque access_tokens (not JWTs).
    // Backend services decode the token as a JWT, so we must send the
    // id_token (which IS a JWT) — matching the old console's behaviour.
    return parsed.id_token ?? parsed.access_token ?? null;
  } catch {
    return null;
  }
}

// Non-workspace top-level routes. First-segment matches here are not
// workspace slugs and must not be forwarded as a signin hint.
const RESERVED_TOP_LEVEL = new Set([
  "callback",
  "components-preview",
  "design-system",
  "home",
  "login",
  "signup",
  "silent-renew.html",
  "_ds",
  "_components",
]);

function extractWorkspaceSlug(target: string): string | null {
  const path = target.split("?")[0] ?? "";
  const first = path.replace(/^\/+/, "").split("/")[0] ?? "";
  if (!first || RESERVED_TOP_LEVEL.has(first)) return null;
  // Workspace slugs are lowercase alphanumeric + hyphens (matches IAM's slug
  // generation). Reject anything else so we never ship a nonsense hint that
  // IAM would have to reject via membership check.
  return /^[a-z0-9][a-z0-9-]*$/.test(first) ? first : null;
}

export function login(returnTo?: string): void {
  const target = returnTo ?? window.location.pathname + window.location.search;
  const slug = extractWorkspaceSlug(target);
  getUserManager().signinRedirect({
    state: { returnTo: target },
    extraQueryParams: slug ? { workspace: slug } : undefined,
  });
}

export async function logout(): Promise<void> {
  try {
    const um = getUserManager();

    // Stop silent renew to prevent token re-acquisition during logout
    um.stopSilentRenew();

    // Grab id_token BEFORE clearing state — OIDC provider needs it
    // as id_token_hint to identify and destroy the server-side session
    const user = await um.getUser();
    const idToken = user?.id_token;

    console.log('[auth] logout: id_token present?', !!idToken);
    console.log('[auth] logout: post_logout_redirect_uri', `${OIDC_AUTHORITY}/logout-success`);

    // Clear local OIDC state
    await um.removeUser();
    await um.clearStaleState();

    // Redirect to IAM for server-side session destruction
    await um.signoutRedirect({ id_token_hint: idToken });
  } catch (err) {
    console.error('[auth] logout failed:', err);
    // Fallback: manual redirect to end session
    window.location.href = `${OIDC_AUTHORITY}/session/end?post_logout_redirect_uri=${encodeURIComponent(OIDC_AUTHORITY + '/logout-success')}`;
  }
}
